import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react"
import type { VariantProps } from "class-variance-authority"
import Command from "@/v2-ui/command"
import Icon from "@/v2-ui/icon"
import Popover, { type PopoverContentProps } from "@/v2-ui/popover"
import cn from "@/v2-ui/utils/utils.cn"
import { usePrimitiveText } from "@/v2-ui/text/offline"
import {
  MULTI_SELECT_SEARCH_THRESHOLD,
  multiSelectTriggerVariants,
} from "./multiSelect.constants"
import {
  collapseSelectedOptions,
  collectLeafValues,
  MultiSelectTreePanel,
} from "./MultiSelectTree"
import { highlightLabel } from "./multiSelect.highlight"
import {
  resolveMultiSelectOpenSide,
  type MultiSelectResolvedSide,
} from "./multiSelect.utils"

export type MultiSelectOption = {
  value: string
  label: string
  /**
   * Optional disabled flag — passed straight through to `cmdk`'s
   * `Command.Item`. Disabled items are shown but not selectable.
   */
  disabled?: boolean
  /** Optional leading node (e.g. a colored dot or flag) rendered before the label. */
  icon?: ReactNode
  /**
   * Optional trailing node rendered right-aligned after the label (the label
   * takes the remaining space, this hugs the right edge). Handy for a facet
   * count or badge. Style it at the call site — the slot only owns layout.
   */
  trailing?: ReactNode
  /**
   * Optional rich body rendered in place of the label text inside the
   * dropdown item (e.g. an avatar + name + email stack). The plain `label`
   * is still what search matches and what the trigger summary shows.
   */
  content?: ReactNode
  /** Extra search terms beyond the label (e.g. an email). */
  keywords?: string[]
  /**
   * Nested options — presence of children on any option switches the panel
   * into tree mode (MultiSelectTreePanel): collapsed rows, lazy-mounted
   * children, parent toggles its descendant leaves. Selection currency is
   * leaf values only.
   */
  children?: MultiSelectOption[]
  /**
   * Async branch: the node is expandable although its `children` are not
   * loaded yet. Opening it fires the panel's `onExpand`; the consumer then
   * serves the fetched rows as `children` (and `isLoadingChildren` while
   * the fetch runs). Until children arrive the node is its own selection
   * unit — ticking it selects `value` itself.
   */
  hasChildren?: boolean
  /** With `hasChildren`: renders a loading row under the open node. */
  isLoadingChildren?: boolean
  /**
   * Section key — renders the option under the matching `groups` heading
   * (flat panel only). Options without a key render first, ungrouped.
   */
  group?: string
}

/** A section heading for options carrying the same `group` key. */
export type MultiSelectGroup = { key: string; heading: ReactNode }

/**
 * True for an option that renders as a tree branch: loaded children, or an
 * async branch (`hasChildren`) whose children arrive on expand. THE shape test
 * the panel dispatches on and the trigger's chip collapse uses.
 */
export function isMultiSelectBranch(option: MultiSelectOption): boolean {
  return (option.children?.length ?? 0) > 0 || option.hasChildren === true
}

type MultiSelectVariantProps = VariantProps<typeof multiSelectTriggerVariants>

export type MultiSelectText = {
  selectAll?: string
  clear?: string
}

export type MultiSelectProps = MultiSelectVariantProps &
  Pick<PopoverContentProps, "align" | "sideOffset"> &
  Pick<
    MultiSelectPanelProps,
    "search" | "onSearchChange" | "isLoading" | "groups" | "onExpand"
  > & {
    value: string[]
    onChange: (value: string[]) => void
    options: MultiSelectOption[]
    /** Overrides for offline UI-chrome strings (selectAll, clear). */
    text?: MultiSelectText
    /** Trigger placeholder when nothing is selected. Defaults to the translated "Select…". */
    placeholder?: string
    /**
     * Replaces the trigger's default content (selection summary + caret)
     * entirely — for triggers that act as a plain button (e.g. "Add more")
     * while the selection is rendered elsewhere.
     */
    triggerContent?: ReactNode
    /**
     * How the trigger renders the current selection:
     * - `summary` (default): text summary — `"A, B +3"`.
     * - `chips`: the first selections as removable chips (each with its own
     *   X deselecting just that value) plus a `+N` overflow chip. Chips stay
     *   on one row inside the trigger's fixed height — they truncate, never
     *   wrap, so the trigger footprint is identical to `summary`.
     */
    triggerVariant?: "summary" | "chips"
    /**
     * `chips` only: render each selected chip yourself (nav-wrapped label,
     * object icon, your own pill shell) — `remove` deselects that option.
     * Your chip may carry links/buttons: the trigger then renders as a
     * `div[role=combobox]` instead of a `button`, since interactive content
     * inside a button is invalid HTML and its clicks would toggle the
     * popover.
     */
    renderChip?: (option: MultiSelectOption, remove: () => void) => ReactNode
    /**
     * `chips` only: let the chips wrap onto further rows (the trigger grows
     * from its size's height to fit them) and raise the overflow cap to
     * {@link MULTI_SELECT_WRAPPED_CHIP_CAP}. Default: one fixed-height row,
     * chips truncate, overflow collapses to `+N`.
     */
    chipsWrap?: boolean
    /**
     * Search input placeholder shown when the dropdown contains more
     * than {@link MULTI_SELECT_SEARCH_THRESHOLD} options. Defaults to
     * `"Search…"`.
     */
    searchPlaceholder?: string
    /**
     * Number of options at which the in-popover search input appears.
     * Defaults to {@link MULTI_SELECT_SEARCH_THRESHOLD} (8). Pass
     * `Number.POSITIVE_INFINITY` to never show search; `0` to always show.
     */
    searchThreshold?: number
    /** Body shown when the search filters everything out. Defaults to the translated "No matches". */
    emptyMessage?: ReactNode
    /**
     * Shows a "Select all" / "Clear" action row at the foot of the dropdown.
     * Defaults to `true` — bulk toggling is almost always wanted on a
     * multi-select, and it makes "deselect everything" a single click.
     */
    enableSelectAll?: boolean
    /**
     * Render the popover modally. Needed when the MultiSelect is mounted
     * inside a Dialog: the dialog's scroll-lock blocks wheel events on
     * body-portaled content, and a modal popover registers its own scroll
     * allowance so the option list can scroll.
     */
    modal?: boolean
    disabled?: boolean
    /** Muted header line rendered at the top of the popover. */
    header?: ReactNode
    /**
     * Side the popover opens on. Default `"auto"` measures the trigger once
     * per open and picks the top only when the bottom offers less than the
     * preferred height and the top offers more (a trigger near the bottom
     * of a sidebar); the side then stays fixed until close. `"top"` and
     * `"bottom"` pin it.
     */
    side?: "auto" | MultiSelectResolvedSide
    /**
     * Let the popover flip to the other side when the list does not fit.
     * Defaults to `false`: the side is fixed at open and the list shrinks
     * to the available height instead. A variable-height list (async
     * suggestions, search filtering) would otherwise hop between top and
     * bottom on every result change, since Floating UI re-runs collision
     * detection on each content resize. `side="auto"` covers the cramped
     * case without that.
     */
    avoidCollisions?: boolean
    /**
     * Label for a selected value that has no option (async suggest lists,
     * where `options` is only the current suggestions). Default: such a
     * value is not shown in the trigger. When set, chips render in
     * selection order rather than catalog order.
     */
    resolveLabel?: (value: string) => string
    contentClassName?: string
    className?: string
    /** Tested IDs / aria-label passthrough on the trigger. */
    triggerProps?: Omit<
      ComponentProps<"button">,
      "type" | "className" | "disabled" | "onClick" | "children"
    >
  }

export type MultiSelectPanelProps = {
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  /** Overrides for offline UI-chrome strings (selectAll, clear). */
  text?: MultiSelectText
  searchPlaceholder?: string
  searchThreshold?: number
  emptyMessage?: ReactNode
  enableSelectAll?: boolean
  /**
   * Focus the search input as soon as the panel mounts, so the user can
   * type immediately (e.g. when the panel opens from a hover submenu).
   */
  autoFocusSearch?: boolean
  /**
   * Wrap the matching substring of option labels in a subtle mark while
   * searching (flat and tree panels). Display-only; defaults to true.
   */
  highlightSearch?: boolean
  /**
   * Controlled search term (async suggest lists): the consumer owns the
   * term and serves `options` already matched against it, so the input
   * always shows and cmdk's own filtering is off. Flat panel only — the tree
   * panel keeps its own term and only NOTIFIES through `onSearchChange`, so
   * a consumer with async branches can fetch what a term may reveal.
   */
  search?: string
  onSearchChange?: (search: string) => void
  /**
   * Renders a loading indicator: the flat panel in its empty slot while the
   * option source runs, the tree panel as a trailing row while async
   * branches or a search are being fetched.
   */
  isLoading?: boolean
  /**
   * Tree panel only: a branch was opened. The hook for async branches
   * (`hasChildren` options): fetch the node's children and serve them via
   * `options`. Fires on every open, so the consumer dedupes.
   */
  onExpand?: (option: MultiSelectOption) => void
  /** Section headings for grouped options, in render order. Flat panel only. */
  groups?: MultiSelectGroup[]
}

/**
 * The cmdk body of a MultiSelect — search input, option list and the
 * "Select all" / "Clear" footer — without the trigger/popover shell.
 * Use it to embed the exact MultiSelect experience inside another
 * floating surface (e.g. a DropdownMenu submenu).
 */
export function MultiSelectPanel(props: MultiSelectPanelProps) {
  // Dispatch on shape, not state: nested options render the tree panel,
  // flat options the cmdk list. Both are separate components so each keeps
  // its own hook order regardless of which one a given options prop picks.
  const isTree = props.options.some(isMultiSelectBranch)
  if (isTree) {
    return (
      <MultiSelectTreePanel
        value={props.value}
        onChange={props.onChange}
        options={props.options}
        text={props.text}
        searchPlaceholder={props.searchPlaceholder}
        emptyMessage={props.emptyMessage}
        enableSelectAll={props.enableSelectAll}
        autoFocusSearch={props.autoFocusSearch}
        highlightSearch={props.highlightSearch}
        onExpand={props.onExpand}
        onSearchChange={props.onSearchChange}
        isLoading={props.isLoading}
      />
    )
  }
  return <MultiSelectFlatPanel {...props} />
}

function MultiSelectFlatPanel(props: MultiSelectPanelProps) {
  const { getText } = usePrimitiveText()
  const {
    value,
    onChange,
    options,
    text,
    searchPlaceholder = getText("UI_Primitive_MultiSelect_SearchPlaceholder")
      .Name,
    searchThreshold = MULTI_SELECT_SEARCH_THRESHOLD,
    emptyMessage = getText("UI_Primitive_MultiSelect_NoMatches").Name,
    enableSelectAll = true,
    autoFocusSearch,
    highlightSearch = true,
    search: controlledSearch,
    onSearchChange,
    isLoading,
    groups,
  } = props
  const selectAllLabel =
    text?.selectAll ?? getText("UI_Primitive_MultiSelect_SelectAll").Name
  const clearLabel =
    text?.clear ?? getText("UI_Primitive_MultiSelect_Clear").Name

  const selectedSet = useMemo(() => new Set(value), [value])
  // Consumer-owned search: the options ARE the matches, so the input always
  // shows and cmdk must not filter them again.
  const isControlledSearch = onSearchChange != null
  const showSearch = isControlledSearch || options.length > searchThreshold

  // Controlled search purely to pin the list to the top on every filter
  // change — cmdk otherwise keeps its selected item in view, which can
  // scroll the first match out of sight.
  const [internalSearch, setInternalSearch] = useState("")
  const search = isControlledSearch ? (controlledSearch ?? "") : internalSearch
  const setSearch = isControlledSearch ? onSearchChange : setInternalSearch
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 })
  }, [search])

  const toggle = (optionValue: string) => {
    if (selectedSet.has(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const selectableValues = useMemo(
    () => options.filter((opt) => !opt.disabled).map((opt) => opt.value),
    [options],
  )
  const allSelected =
    selectableValues.length > 0 &&
    selectableValues.every((v) => selectedSet.has(v))
  const noneSelected = value.length === 0
  const showSelectAll = enableSelectAll && options.length > 1

  function renderItem(option: MultiSelectOption) {
    const isSelected = selectedSet.has(option.value)
    return (
      <Command.Item
        key={option.value}
        // cmdk uses `value` as the item's identity — options sharing
        // a label (two users with the same name) would otherwise
        // highlight and toggle as one item. The label stays
        // searchable via `keywords`.
        value={option.value}
        keywords={[option.label, ...(option.keywords ?? [])]}
        disabled={option.disabled}
        onSelect={() => toggle(option.value)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-sm border",
            isSelected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input bg-transparent",
          )}
          aria-hidden
        >
          {isSelected ? (
            <Icon src="check" size={12} className="text-current" />
          ) : null}
        </span>
        {option.icon != null ? (
          <span className="flex shrink-0 items-center">{option.icon}</span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">
          {option.content ??
            (highlightSearch && search !== ""
              ? highlightLabel(option.label, search)
              : option.label)}
        </span>
        {option.trailing != null ? (
          <span className="ml-2 shrink-0">{option.trailing}</span>
        ) : null}
      </Command.Item>
    )
  }

  return (
    <Command shouldFilter={!isControlledSearch}>
      {showSearch ? (
        <Command.Input
          autoFocus={autoFocusSearch}
          value={search}
          onValueChange={setSearch}
          placeholder={searchPlaceholder}
        />
      ) : null}
      <Command.List ref={listRef}>
        <Command.Empty>
          {isLoading ? (
            <Icon
              loading
              size={16}
              className="mx-auto text-muted-foreground"
              aria-hidden
            />
          ) : (
            emptyMessage
          )}
        </Command.Empty>
        {groups == null ? (
          options.map(renderItem)
        ) : (
          <>
            {options.filter((option) => option.group == null).map(renderItem)}
            {groups.map((group) => {
              const members = options.filter(
                (option) => option.group === group.key,
              )
              if (members.length === 0) return null
              return (
                <Command.Group key={group.key} heading={group.heading}>
                  {members.map(renderItem)}
                </Command.Group>
              )
            })}
          </>
        )}
        {isLoading && options.length > 0 ? (
          // The empty slot above only shows while the list is EMPTY; a
          // consumer-owned search that keeps rows around while it fetches (a
          // pinned option, an earlier page) gets the same indicator as a
          // trailing row, centered like the empty-slot loader. A plain div,
          // not a Command.Item, so cmdk never filters or focuses it.
          <div
            className="flex h-12 items-center justify-center"
            aria-busy
            role="presentation"
          >
            <Icon loading size={16} className="text-muted-foreground" />
          </div>
        ) : null}
      </Command.List>
      {showSelectAll ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-1 py-1">
          <button
            type="button"
            disabled={allSelected}
            data-feature-code="UI_Primitive_MultiSelect_SelectAll"
            className="rounded-sm px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onChange(selectableValues)}
          >
            {selectAllLabel}
          </button>
          <button
            type="button"
            disabled={noneSelected}
            data-feature-code="UI_Primitive_MultiSelect_Clear"
            className="rounded-sm px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onChange([])}
          >
            {clearLabel}
          </button>
        </div>
      ) : null}
    </Command>
  )
}

/**
 * Multi-value Select with the same trigger chrome as `Select` — same CVA
 * `size` / `variant` axes, same h-6 / h-7 / h-8 / h-9 / h-10 sizings — so
 * the two compose seamlessly inside forms and filter popovers. Body is a
 * `cmdk` Command list rendered inside `Popover.Content`; a search input
 * appears automatically when the option count crosses
 * {@link MULTI_SELECT_SEARCH_THRESHOLD}.
 *
 * Controlled-only on purpose. Filterable surfaces want the value to live
 * in `state.filters`, not in component-local state — uncontrolled mode
 * would split that ownership and make "Reset filters" half-effective.
 */
// Max value chips rendered in the `chips` trigger variant before the rest
// collapses into a single `+N` chip. Mirrors the 2-label cap of the text
// summary, so both variants communicate the same amount before eliding.
const MULTI_SELECT_TRIGGER_CHIP_CAP = 2
// Chip cap for a wrapping trigger — a 500-value selection must not become
// 500 chips; the rest is the `+N` chip.
const MULTI_SELECT_WRAPPED_CHIP_CAP = 12

// A wrapping trigger trades its size's fixed height for the same minimum.
const WRAPPED_TRIGGER_CLASSNAME: Record<
  NonNullable<MultiSelectVariantProps["size"]>,
  string
> = {
  tiny: "h-auto min-h-6 py-0.5",
  xs: "h-auto min-h-7 py-0.5",
  sm: "h-auto min-h-8 py-1",
  md: "h-auto min-h-9 py-1",
  lg: "h-auto min-h-10 py-1.5",
}

function MultiSelect(props: MultiSelectProps) {
  const { getText } = usePrimitiveText()
  const {
    value,
    onChange,
    options,
    text,
    placeholder = getText("UI_Primitive_MultiSelect_Placeholder").Name,
    triggerVariant = "summary",
    triggerContent,
    renderChip,
    chipsWrap = false,
    searchPlaceholder,
    searchThreshold = MULTI_SELECT_SEARCH_THRESHOLD,
    emptyMessage,
    enableSelectAll = true,
    modal,
    disabled,
    header,
    avoidCollisions = false,
    resolveLabel,
    search,
    onSearchChange,
    isLoading,
    groups,
    onExpand,
    size,
    variant,
    align = "start",
    side = "auto",
    sideOffset = 4,
    className,
    contentClassName,
    triggerProps,
  } = props
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLElement>(null)
  // Resolved once per open (see resolveMultiSelectOpenSide) so the side is
  // stable while the list resizes; a pinned `side` prop is passed through.
  const [autoSide, setAutoSide] = useState<MultiSelectResolvedSide>("bottom")
  const resolvedSide = side === "auto" ? autoSide : side

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next && side === "auto" && triggerRef.current) {
        setAutoSide(
          resolveMultiSelectOpenSide({
            trigger: triggerRef.current,
            sideOffset,
          }),
        )
      }
      setOpen(next)
    },
    [side, sideOffset],
  )
  const selectedSet = useMemo(() => new Set(value), [value])

  // Flattened for label lookup — in tree mode the selected leaves are nested.
  const flatOptions = useMemo(() => {
    const out: MultiSelectOption[] = []
    const stack = [...options]
    while (stack.length > 0) {
      const opt = stack.shift() as MultiSelectOption
      out.push(opt)
      if (opt.children?.length) stack.unshift(...opt.children)
    }
    return out
  }, [options])

  // Options for the current selection, in catalog order. A tree (any option
  // with children — the same shape test MultiSelectPanel dispatches on)
  // collapses fully selected subtrees to their parent, so "all of Oslo" is one
  // chip, not every postal code in it. With `resolveLabel` the selection order
  // wins and values missing from `options` get a synthesized option, so an
  // async list's earlier picks keep their chips.
  const isTree = options.some(isMultiSelectBranch)
  const selectedOptions = useMemo<MultiSelectOption[]>(() => {
    if (selectedSet.size === 0) return []
    if (resolveLabel == null) {
      return isTree
        ? collapseSelectedOptions(options, selectedSet)
        : flatOptions.filter((opt) => selectedSet.has(opt.value))
    }
    return value.map(
      (selected) =>
        flatOptions.find((opt) => opt.value === selected) ?? {
          value: selected,
          label: resolveLabel(selected),
        },
    )
  }, [options, flatOptions, isTree, selectedSet, value, resolveLabel])

  const triggerLabel = useMemo(() => {
    const selectedLabels = selectedOptions.map((opt) => opt.label)
    if (selectedLabels.length === 0) return null
    if (selectedLabels.length <= 2) return selectedLabels.join(", ")
    return `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`
  }, [selectedOptions])

  const clearLabel =
    text?.clear ?? getText("UI_Primitive_MultiSelect_Clear").Name

  // A collapsed parent chip stands for its whole subtree, so its X drops
  // every leaf under it (a leaf's leaf set is itself).
  function removeOption(option: MultiSelectOption) {
    const drop = new Set(collectLeafValues(option))
    onChange(value.filter((v) => !drop.has(v)))
  }

  const chipCap = chipsWrap
    ? MULTI_SELECT_WRAPPED_CHIP_CAP
    : MULTI_SELECT_TRIGGER_CHIP_CAP
  const triggerClassName = cn(
    multiSelectTriggerVariants({ size, variant }),
    chipsWrap && WRAPPED_TRIGGER_CLASSNAME[size ?? "md"],
    className,
  )
  const triggerBody = triggerContent ?? (
    <>
      {triggerVariant === "chips" && selectedOptions.length > 0 ? (
        // `!flex` beats the trigger chrome's `[&>span]:line-clamp-1`
        // (which would set display:-webkit-box and kill the row).
        <span
          className={cn(
            "!flex min-w-0 flex-1 items-center gap-1 text-left",
            chipsWrap ? "flex-wrap" : "overflow-hidden",
          )}
        >
          {selectedOptions.slice(0, chipCap).map((opt) =>
            renderChip ? (
              // A consumer chip is interactive (its X, a nav link): a click
              // inside it acts on the chip and must not reach the trigger's
              // popover toggle.
              <span
                key={opt.value}
                className="contents"
                onClick={(e) => e.stopPropagation()}
              >
                {renderChip(opt, () => removeOption(opt))}
              </span>
            ) : (
              <span
                key={opt.value}
                className="flex min-w-0 shrink items-center gap-0.5 rounded-sm border border-border bg-secondary py-px pl-1 pr-0.5 text-xs leading-none text-secondary-foreground"
              >
                <span className="min-w-0 truncate py-0.5">{opt.label}</span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`${clearLabel}: ${opt.label}`}
                  data-feature-code="UI_Primitive_MultiSelect_Clear"
                  className="shrink-0 cursor-pointer rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  onClick={(e) => {
                    // Deselect just this value without toggling the
                    // popover open (the click bubbles to the trigger).
                    e.stopPropagation()
                    removeOption(opt)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      e.stopPropagation()
                      removeOption(opt)
                    }
                  }}
                >
                  <Icon src="cross-small" size={12} className="text-current" />
                </span>
              </span>
            ),
          )}
          {selectedOptions.length > chipCap && (
            <span className="shrink-0 rounded-sm border border-border bg-secondary px-1 py-0.5 text-xs leading-none text-secondary-foreground">
              {`+${selectedOptions.length - chipCap}`}
            </span>
          )}
        </span>
      ) : (
        <span
          className={cn(
            "min-w-0 truncate text-left",
            triggerLabel == null && "text-muted-foreground",
          )}
        >
          {triggerLabel ?? placeholder}
        </span>
      )}
      <Icon
        src="caret-down-custom"
        size={16}
        className="ml-2 shrink-0 self-center text-muted-foreground"
      />
    </>
  )

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        {renderChip ? (
          // Consumer chips may hold links and buttons, so the trigger is a
          // div: keyboard open/close is re-implemented here (Radix wires the
          // click), `disabled` becomes aria + pointer/tab blocking.
          <div
            ref={triggerRef as RefObject<HTMLDivElement>}
            role="combobox"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            aria-haspopup="listbox"
            aria-expanded={open}
            onKeyDown={(e) => {
              if (disabled) return
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleOpenChange(!open)
              }
            }}
            className={cn(
              triggerClassName,
              disabled && "pointer-events-none opacity-50",
            )}
            // The button-typed passthrough (ids, aria-label) is generic
            // HTML attributes in practice; narrow it to the div's shape.
            {...(triggerProps as Omit<ComponentProps<"div">, "children">)}
          >
            {triggerBody}
          </div>
        ) : (
          <button
            ref={triggerRef as RefObject<HTMLButtonElement>}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={triggerClassName}
            {...triggerProps}
          >
            {triggerBody}
          </button>
        )}
      </Popover.Trigger>
      <Popover.Content
        size="match-trigger"
        spacing={false}
        align={align}
        side={resolvedSide}
        sideOffset={sideOffset}
        avoidCollisions={avoidCollisions}
        // Column flex + available-height cap so the cmdk list (min-h-0 in
        // command.css) shrinks and scrolls instead of overflowing the viewport
        // when the fixed side leaves less room than the list wants.
        className={cn(
          "flex max-h-[var(--radix-popover-content-available-height)] min-w-[180px] flex-col p-1",
          contentClassName,
        )}
      >
        {header != null && (
          <div className="border-b border-border px-2 py-1.5 text-sm font-medium text-foreground">
            {header}
          </div>
        )}
        <MultiSelectPanel
          value={value}
          onChange={onChange}
          options={options}
          text={text}
          searchPlaceholder={searchPlaceholder}
          searchThreshold={searchThreshold}
          emptyMessage={emptyMessage}
          enableSelectAll={enableSelectAll}
          search={search}
          onSearchChange={onSearchChange}
          isLoading={isLoading}
          groups={groups}
          onExpand={onExpand}
        />
      </Popover.Content>
    </Popover>
  )
}

export default MultiSelect
